/* Native Shopify cart drawer.
 * Replaces shopyflow-bridge.js. Pure Shopify Storefront API (/cart/*.js) + section rendering.
 * Open: [data-cart-open]. Close: [data-cart-close] or overlay click or ESC.
 * Quantity inc/dec/remove: data-cart-quantity-* + data-cart-remove inside .cart-item.
 * Add-to-cart: any form[action$="/cart/add"] is intercepted, posted to /cart/add.js, drawer re-renders + opens.
 */
(function () {
  'use strict';

  const SECTION_ID = 'cart-drawer';
  let isOpen = false;
  let lastFocus = null;
  /* Per-line in-flight guard: prevents concurrent /cart/change.js posts from racing
   * (rapid clicks on +/- would each send a stale DOM value and the final qty would be non-deterministic). */
  const inFlightLines = new Set();

  const $drawer = () => document.querySelector('[data-cart-drawer]');
  const $count = () => document.querySelectorAll('[data-cart-count]');

  /* Shared site backdrop (also used by mega-menu) — same blur effect. */
  function $backdrop() {
    let bd = document.querySelector('.mega-menu-backdrop');
    if (!bd) {
      bd = document.createElement('div');
      bd.className = 'mega-menu-backdrop';
      bd.setAttribute('aria-hidden', 'true');
      document.body.appendChild(bd);
    }
    return bd;
  }

  function setCount(n) {
    $count().forEach((el) => {
      el.textContent = String(n);
    });
    /* Drawer title uses [data-cart-count-display] which renders "leer"
       when n === 0, otherwise the numeric count. The navbar badge
       ([data-cart-count]) keeps the raw number. */
    document.querySelectorAll('[data-cart-count-display]').forEach((el) => {
      el.textContent = n === 0 ? 'leer' : String(n);
    });
  }

  /* Surface a transient error message to the user. Console alone isn't enough for storefront UX. */
  function showError(msg) {
    console.error('[cart]', msg);
    let toast = document.getElementById('cart-error-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'cart-error-toast';
      toast.setAttribute('role', 'alert');
      toast.setAttribute('aria-live', 'assertive');
      toast.style.cssText =
        'position:fixed;top:20px;right:20px;z-index:9999;background:#352f19;color:#fcfaf7;padding:14px 20px;border-radius:8px;font-family:Manrope,sans-serif;font-size:14px;max-width:360px;box-shadow:0 4px 16px rgba(0,0,0,.2);opacity:0;transition:opacity .2s ease;';
      document.body.appendChild(toast);
    }
    toast.textContent = msg;
    requestAnimationFrame(() => {
      toast.style.opacity = '1';
    });
    clearTimeout(toast._timer);
    toast._timer = setTimeout(() => {
      toast.style.opacity = '0';
    }, 4000);
  }

  function openDrawer() {
    const d = $drawer();
    if (!d || isOpen) return;
    lastFocus = document.activeElement;
    d.hidden = false;
    const bd = $backdrop();
    requestAnimationFrame(() => {
      d.classList.add('is-open');
      bd.classList.add('is-open');
      document.body.classList.add('cart-is-open');
      const closer = d.querySelector('[data-cart-close]');
      if (closer && typeof closer.focus === 'function') closer.focus();
    });
    isOpen = true;
  }

  function closeDrawer() {
    const d = $drawer();
    if (!d || !isOpen) return;
    d.classList.remove('is-open');
    $backdrop().classList.remove('is-open');
    document.body.classList.remove('cart-is-open');
    setTimeout(() => {
      d.hidden = true;
    }, 300);
    if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
    isOpen = false;
  }

  async function fetchJSON(url, opts) {
    const res = await fetch(
      url,
      Object.assign({ headers: { 'Content-Type': 'application/json', Accept: 'application/json' } }, opts || {}),
    );
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`${url} ${res.status}: ${text}`);
    }
    return res.json();
  }

  async function refreshDrawer() {
    /* Re-render the cart-drawer section by fetching the current path with ?section_id=cart-drawer.
     * Shopify returns just that section's HTML. Requires sections/cart-drawer.liquid (which exists). */
    let html;
    try {
      const res = await fetch(`${window.location.pathname}?section_id=${SECTION_ID}`);
      if (!res.ok) throw new Error(`section fetch ${res.status}`);
      html = await res.text();
    } catch (err) {
      showError('Warenkorb konnte nicht aktualisiert werden. Bitte Seite neu laden.');
      console.error('[cart] refreshDrawer fetch failed:', err);
      return;
    }
    const tmp = document.createElement('div');
    tmp.innerHTML = html.trim();
    const fresh = tmp.querySelector('[data-cart-drawer]');
    const current = $drawer();
    if (!fresh || !current) {
      showError('Warenkorb ist nicht synchron. Bitte Seite neu laden.');
      console.error('[cart] refreshDrawer: section returned no [data-cart-drawer] element', { fresh, current });
      return;
    }
    const wasOpen = current.classList.contains('is-open');
    const wasHidden = current.hidden;
    /* Preserve open state. innerHTML swap keeps the outer element so delegated listeners still hit it. */
    current.innerHTML = fresh.innerHTML;
    current.className = fresh.className + (wasOpen ? ' is-open' : '');
    current.hidden = wasHidden;
  }

  async function changeQuantity(line, qty) {
    if (inFlightLines.has(line)) return; /* drop the request — a previous one is still resolving */
    inFlightLines.add(line);
    try {
      const data = await fetchJSON('/cart/change.js', {
        method: 'POST',
        body: JSON.stringify({ line, quantity: qty }),
      });
      setCount(data.item_count);
      await refreshDrawer();
    } catch (err) {
      showError('Menge konnte nicht aktualisiert werden.');
      console.error('[cart] changeQuantity failed:', err);
    } finally {
      inFlightLines.delete(line);
    }
  }

  async function addToCart(formData) {
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({ description: 'Hinzufügen zum Warenkorb fehlgeschlagen' }));
        throw new Error(errBody.description || 'Hinzufügen zum Warenkorb fehlgeschlagen');
      }
      const cart = await fetchJSON('/cart.js');
      setCount(cart.item_count);
      await refreshDrawer();
      openDrawer();
      return cart;
    } catch (err) {
      console.error('[cart] add failed:', err);
      throw err;
    }
  }

  /* Initial count sync (in case cached HTML is stale). */
  fetchJSON('/cart.js')
    .then((c) => setCount(c.item_count))
    .catch(() => {});

  /* Delegated click handler for open/close + qty buttons + remove. */
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-cart-open]');
    if (opener) {
      e.preventDefault();
      /* Toggle: clicking the cart icon while the drawer is open should close it. */
      if (isOpen) closeDrawer();
      else openDrawer();
      return;
    }
    const closer = e.target.closest('[data-cart-close]');
    if (closer) {
      e.preventDefault();
      closeDrawer();
      return;
    }
    /* Click outside the panel while cart is open: close.
     * Header sits at z:200 — above .inc-cart-overlay (z:100) — so clicks on
     * the navbar never reach the overlay. Catch ANY click outside .inc-cart-panel
     * that isn't on a cart-open trigger (those re-open). */
    if (isOpen) {
      const panel = e.target.closest('.inc-cart-panel');
      const cartTrigger = e.target.closest('[data-cart-open]');
      if (!panel && !cartTrigger) {
        closeDrawer();
        return;
      }
    }
    const item = e.target.closest('[data-cart-item]');
    if (!item) return;
    const line = parseInt(item.dataset.cartLine, 10);
    /* Shopify cart lines are 1-based, so 0 and NaN are both invalid. */
    if (!Number.isInteger(line) || line < 1) return;

    if (e.target.closest('[data-cart-quantity-increase]')) {
      const input = item.querySelector('[data-cart-quantity-input]');
      const qty = (parseInt(input?.value, 10) || 1) + 1;
      changeQuantity(line, qty);
      return;
    }
    if (e.target.closest('[data-cart-quantity-decrease]')) {
      const input = item.querySelector('[data-cart-quantity-input]');
      const qty = Math.max(0, (parseInt(input?.value, 10) || 1) - 1);
      changeQuantity(line, qty);
      return;
    }
    if (e.target.closest('[data-cart-remove]')) {
      e.preventDefault();
      changeQuantity(line, 0);
      return;
    }
  });

  /* Qty input direct change (typed value). */
  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-cart-quantity-input]');
    if (!input) return;
    const item = input.closest('[data-cart-item]');
    if (!item) return;
    const line = parseInt(item.dataset.cartLine, 10);
    if (!Number.isInteger(line) || line < 1) return;
    const qty = Math.max(0, parseInt(input.value, 10) || 0);
    changeQuantity(line, qty);
  });

  /* ESC closes drawer. */
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && isOpen) closeDrawer();
  });

  /* Intercept add-to-cart forms. Any <form action="/cart/add" method="post"> on any page. */
  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    const action = form.getAttribute('action') || '';
    if (!action.match(/\/cart\/add$/) && !form.matches('[data-cart-add-form]')) return;
    e.preventDefault();
    const submitBtn = form.querySelector('[type="submit"]');
    if (submitBtn) submitBtn.setAttribute('disabled', 'disabled');
    addToCart(new FormData(form))
      .catch((err) => {
        showError(err && err.message ? err.message : 'Hinzufügen zum Warenkorb fehlgeschlagen.');
      })
      .finally(() => {
        if (submitBtn) submitBtn.removeAttribute('disabled');
      });
  });

  /* Expose tiny API for sections that need programmatic add (e.g. legacy buttons without a form). */
  window.NativeCart = {
    open: openDrawer,
    close: closeDrawer,
    refresh: refreshDrawer,
    add: addToCart,
    changeQuantity,
  };
})();
